import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { loginAdresa } from "@/lib/prihlasenie";

export const API_URL: string = import.meta.env.VITE_API_URL || "/api";

/**
 * Axios instance pre Tandem Rozpočet API.
 *
 * Interceptory:
 * - Request: pridáva `X-Autor` hlavičku z localStorage (B3 — bez auth,
 *   audit log dostáva meno užívateľa zadané v UserNameDialog).
 * - Response: 401 (expirovaná/chýbajúca session) → redirect na /login.
 *   Ostatné chyby riešia TanStack Query mutations per-hook.
 *
 * Session cookie `tandem_session` je httponly + same-origin — axios ju
 * posiela automaticky, netreba `withCredentials`.
 *
 * Base URL: dev = `/api` (Vite proxy → :8000), prod = `/api` (rovnaký origin).
 */
export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/**
 * HTTP hlavičky musia byť ASCII (RFC 7230). SK znaky (č, š, á, …)
 * encodujeme cez `encodeURIComponent`. Backend ich pri ukladaní do
 * audit_log akceptuje aj v encoded podobe. V R2 s reálnym auth-om
 * sa nahradí user_id FK.
 */
function encodeHeader(value: string): string {
  // ASCII-printable range — žiadny encoding netreba
  // eslint-disable-next-line no-control-regex
  if (/^[\x20-\x7E]*$/.test(value)) return value;
  return encodeURIComponent(value);
}

/**
 * Upload súborov: `FormData` sa NESMIE poslať s `Content-Type: application/json`.
 *
 * Inštancia má JSON Content-Type v defaultoch (nižšie je to takmer vždy
 * správne), ale `transformRequest` v axios v1 sa pri `FormData` pozerá
 * práve naň — a keď vidí JSON, **prevedie FormData na JSON**
 * (`formDataToJSON`). Na server potom príde `{"file":{}}`, teda súbor bez
 * obsahu, a FastAPI vráti 422 „field required". Obsah pritom nikdy
 * neopustí prehliadač.
 *
 * Presne takto bol rozbitý import z TopSolidu (KOLO 80) aj starší CSV
 * import; upload cenníkov prežil len preto, že si hlavičku prepisoval
 * sám. Rieši sa to **tu, na jednom mieste** — spoliehať sa na to, že si
 * na to spomenie každý ďalší upload, je presne ten vzor, ktorý nás už
 * dvakrát stál nasadenie.
 *
 * Hlavička sa **zhadzuje**, nenastavuje na `multipart/form-data`:
 * multipart potrebuje `boundary`, ktoré vie doplniť len prehliadač.
 */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers?.setContentType) {
      config.headers.setContentType(false);
    } else {
      delete (config.headers as Record<string, unknown>)["Content-Type"];
    }
  }
  return config;
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const userName =
    typeof window !== "undefined"
      ? window.localStorage.getItem("tandem_user_name")
      : null;
  const autor = encodeHeader(userName?.trim() || "anonymous");
  // axios v1 — headers je AxiosHeaders s `.set()` metódou
  if (config.headers?.set) {
    config.headers.set("X-Autor", autor);
  } else {
    (config.headers as Record<string, string>)["X-Autor"] = autor;
  }
  return config;
});

/**
 * 401 = session expirovala alebo chýba → presmeruj na /login.
 * `/api/auth/me` 401 nikdy nevráti (je verejný), ale guard na pathname
 * bráni redirect-loopu keď už na /login sme.
 */
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (
      error.response?.status === 401 &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/login"
    ) {
      // S `?next=`, aby sa po prihlásení vrátil tam, kde bol — vrátane
      // query (odkaz `?koncept=` z Tandem Zostáv, KOLO 127).
      const { pathname, search, hash } = window.location;
      window.location.assign(loginAdresa(`${pathname}${search}${hash}`));
    }
    return Promise.reject(error);
  },
);

/**
 * Vytiahne SK chybovú správu z FastAPI ErrorResponse payloadu.
 * Backend vracia `{ error, message, field }` v `response.data.detail`
 * (HTTPException default), alebo priamo (custom handler).
 */
export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const err = error as AxiosError<{
      detail?: { error?: string; message?: string; field?: string };
      error?: string;
      message?: string;
    }>;
    const detail = err.response?.data?.detail;
    if (detail?.message) return detail.message;
    if (err.response?.data?.message) return err.response.data.message;
    if (err.message) return err.message;
  }
  if (error instanceof Error) return error.message;
  return "Neznáma chyba";
}

export { axios };
